import { useSelector } from 'react-redux';

import { getGlobalFilters, getParameterOverrides } from 'store/selectors/DataSourceSelectors';
import { filtersAreEquivalent } from 'lib/FilterUtils';

import { Measure } from 'common/performance_measures/types';
import { GlobalFilters, GlobalParameterOverrides } from 'types';
import { cloneDeep, isEqual } from 'lodash';
import { FILTER_FUNCTION, FilterDataType, SoqlFilter } from 'common/components/FilterBar/SoqlFilter';

export const useMeasureConfigWithGlobalFilterParameters = (measure: Measure | null): Measure | null => {
  const additionalFilters = useSelector(
    getGlobalFilters,
    (filters: GlobalFilters, newFilters: GlobalFilters) => {
      return filtersAreEquivalent(filters, newFilters);
    },
  );

  const parameterOverrides = useSelector(
    getParameterOverrides,
    (overrides: GlobalParameterOverrides, newOverrides: GlobalParameterOverrides) => {
      return isEqual(overrides, newOverrides);
    },
  );

  if (!measure?.metricConfig) {
    return measure;
  }

  const dataSourceUid = measure?.dataSourceLensUid ?? '';

  const updatedMeasure = cloneDeep(measure);

  const filtersForThisMeasure = additionalFilters[dataSourceUid] ?? [];

  const allowedMeasureFilters = getAllowedFilters(filtersForThisMeasure);

  if (allowedMeasureFilters.length > 0) {
    updatedMeasure.metricConfig.additionalFilters = allowedMeasureFilters;
  }

  if ((parameterOverrides[dataSourceUid] ?? []).length > 0) {
    updatedMeasure.metricConfig.parameterOverrides = parameterOverrides[dataSourceUid];
  }

  return updatedMeasure;
};

export const getAllowedFilters = (filtersForThisMeasure: SoqlFilter[]): SoqlFilter[] => {
  return filtersForThisMeasure
    .filter((fil) => {
      if (fil?.dataTypeName === FilterDataType.DATE) {
        return false;
      }
      return fil?.function !== FILTER_FUNCTION.NOOP && fil?.columns?.length > 0;
    })
    .map((fil) => ({ ...fil, isOverridden: true }));
};
